import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
    type AgentCodeContext,
    type Agent,
    type AgentExecution,
    type SendToAgentExecutionRequest,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import type { PaginationResponse } from "gitpod-next-api/gitpod/v1/pagination_pb";
import type { GitpodAPI } from "@/api";
import { evictFromCache } from "@/utils/stream-cache";
import { autoPaginate } from "@/queries/pagination";

export const Agents = {
    Ona: {
        id: "00000000-0000-0000-0000-000000006100",
    },
    DevelopmentEnvironmentSetup: {
        id: "0194a7d3-1bdd-7174-9328-c93e91c524a6",
    },
};

export type PlainAgent = PlainMessage<Agent>;
export type PlainAgentExecution = PlainMessage<AgentExecution>;
type PlainAgentExecutionList = {
    agentExecutions: PlainAgentExecution[];
    pagination: PaginationResponse | undefined;
};

const toPlainAgent = (agent: Agent): PlainAgent => {
    return toPlainMessage(agent);
};

export const toPlainAgentExecution = (agentExecution: AgentExecution): PlainAgentExecution => {
    return toPlainMessage(agentExecution);
};

export const agentServiceQueryKey = {
    listAgents: () => keyWithPrincipal(["agents", "list"]),
    listAgentExecutions: () => keyWithPrincipal(["agent-execution", "list"]),
    getAgentExecution: (agentExecutionId?: string) => keyWithPrincipal(["agent-execution", "get", agentExecutionId]),
    createAgentExecutionConversationToken: (agentExecutionId?: string) =>
        keyWithPrincipal(["agent-execution", "create-conversation-token", agentExecutionId]),
};

export const handleAgentExecutionEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
        const agentExecution = await api.agentService.getAgentExecution({ agentExecutionId: evt.resourceId });
        setAgentExecutionInCache(
            client,
            evt.resourceId,
            agentExecution.agentExecution ? toPlainAgentExecution(agentExecution.agentExecution) : undefined,
        );
    } else if (evt.operation === ResourceOperation.CREATE) {
        const agentExecution = await api.agentService.getAgentExecution({ agentExecutionId: evt.resourceId });
        setAgentExecutionInCache(
            client,
            evt.resourceId,
            agentExecution.agentExecution ? toPlainAgentExecution(agentExecution.agentExecution) : undefined,
        );
        await client.invalidateQueries({ queryKey: agentServiceQueryKey.listAgentExecutions() });
    } else if (evt.operation === ResourceOperation.DELETE) {
        setAgentExecutionInCache(client, evt.resourceId, undefined);
    }
};

export const useListAgents = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return useQuery({
        queryKey: agentServiceQueryKey.listAgents(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const agents = await autoPaginate(
                (params) => api.agentService.listAgents(params),
                {},
                (response) => response.agents,
            );
            return {
                agents: agents.map(toPlainAgent),
            };
        },
        enabled: !!user,
    });
};

export const useListAgentExecutions = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: agentServiceQueryKey.listAgentExecutions(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const agentExecutions = await autoPaginate(
                (params) => api.agentService.listAgentExecutions(params),
                {
                    filter: {
                        creatorIds: [user.id],
                    },
                },
                (response) => response.agentExecutions,
            );
            return {
                agentExecutions: agentExecutions.map(toPlainAgentExecution),
            };
        },
        enabled: !!user,
    });
};

export const useGetAgentExecution = (agentExecutionId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: agentServiceQueryKey.getAgentExecution(agentExecutionId),
        queryFn: async () => {
            const response = await api.agentService.getAgentExecution({
                agentExecutionId,
            });
            if (!response.agentExecution) {
                throw new Error("Agent execution not found");
            }
            return toPlainAgentExecution(response.agentExecution);
        },
        enabled: !!agentExecutionId,
    });
};

export const useCreateAgentExecution = () => {
    const api = useGitpodAPI();
    return useMutation({
        mutationFn: async ({ agentId, codeContext }: { agentId: string; codeContext?: AgentCodeContext }) => {
            const response = await api.agentService.startAgent({
                agentId: agentId,
                codeContext: codeContext,
            });
            return {
                agentExecutionId: response.agentExecutionId,
            };
        },
    });
};

export const useDeleteAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agentExecutionId }: { agentExecutionId: string }) => {
            const response = await api.agentService.deleteAgentExecution({
                agentExecutionId,
            });
            return response;
        },
        onSuccess: (_data, variables) => {
            setAgentExecutionInCache(queryClient, variables.agentExecutionId, undefined);
        },
    });
};

export const useStopAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agentExecutionId }: { agentExecutionId: string }) => {
            const response = await api.agentService.stopAgentExecution({
                agentExecutionId,
            });
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: agentServiceQueryKey.getAgentExecution(variables.agentExecutionId),
            });
        },
    });
};

export const useSendToAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: SendToAgentExecutionRequest) => {
            const response = await api.agentService.sendToAgentExecution(request);
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: agentServiceQueryKey.getAgentExecution(variables.agentExecutionId),
            });
        },
    });
};

export const useCreateAgentExecutionConversationToken = (agentExecutionId?: string) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45; // 45 minutes
    return useQuery<string, Error>({
        queryKey: agentServiceQueryKey.createAgentExecutionConversationToken(agentExecutionId),
        queryFn: async (): Promise<string> => {
            if (!agentExecutionId) {
                throw new Error("No agent execution ID provided");
            }

            const { token } = await api.agentService.createAgentExecutionConversationToken({ agentExecutionId });
            if (!token) {
                throw new Error("Failed to create token");
            }

            return token;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!agentExecutionId,
    });
};

function setAgentExecutionInCache(client: QueryClient, agentExecutionId: string, agentExecution?: PlainAgentExecution) {
    client.setQueryData(
        agentServiceQueryKey.getAgentExecution(agentExecutionId),
        agentExecution ? agentExecution : null,
    );
    // We're only updating existing agent executions or removing them.
    // For creations we're invalidating the list of agent executions instead to preserve the backend-ordering of the cache
    client.setQueryData(agentServiceQueryKey.listAgentExecutions(), (currentData?: PlainAgentExecutionList) => {
        if (!currentData) {
            return currentData;
        }
        return {
            ...currentData,
            agentExecutions: currentData.agentExecutions
                .map((w: PlainAgentExecution) => {
                    if (w.id !== agentExecutionId) {
                        return w;
                    }
                    return agentExecution;
                })
                .filter((w) => !!w),
        };
    });

    if (!agentExecution) {
        void evictFromCache("conversation:" + agentExecutionId);
    }
}
